import React, { useState, useEffect, useContext } from "react";
import {
  Paper,
  TablePagination,
  Typography,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Tooltip,
  IconButton,
  CircularProgress,
  Tabs,
  Tab,
} from "@mui/material";
import Collapse from '@mui/material/Collapse'
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { Alert, AlertTitle } from "@mui/material";
import ProgressBar from "../progress/progressBar";
import Api from "../../service/api";
import TrackHcaExportFile from "./TrackHcaExportFile";
import DateTranslatedPicker from "../admin-track-request/datePicker";
import UserContext from "../../context/user";
import popUpMessage from "../../config/popUpMsg.json";
import "../admin-track-request/admin-track-request.css";

const ExpandMore = styled((props) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme, expand }) => ({
  transform: !expand ? "rotate(0deg)" : "rotate(180deg)",
  marginLeft: "auto",
  transition: theme.transitions.create("transform", {
    duration: theme.transitions.duration.shortest,
  }),
}));

const normalizeStatusKey = (status) =>
  String(status || "")
    .trim()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .toUpperCase()
    .replace("NOTFOUND", "NOT FOUND");

const getStatusLabel = (status) =>
  normalizeStatusKey(status)
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const isNotFoundStatus = (status) => normalizeStatusKey(status) === "NOT FOUND";

const getStatusStyle = (status) => {
  const normalizedStatus = normalizeStatusKey(status);
  switch (normalizedStatus) {
    case "MATCHED":
      return { backgroundColor: "#28a745", color: "#ffffff", border: "1px solid #28a745" };
    case "NOT FOUND":
      return { backgroundColor: "#dc3545", color: "#ffffff", border: "1px solid #dc3545" };
    case "PENDING":
      return { backgroundColor: "#fd7e14", color: "#ffffff", border: "1px solid #fd7e14" };
    case "EXPIRED":
      return { backgroundColor: "#dc3545", color: "#ffffff", border: "1px solid #dc3545" };
    case "ERROR":
      return { backgroundColor: "#dc3545", color: "#ffffff", border: "1px solid #dc3545" };
    case "REQUESTED":
      return { backgroundColor: "#0d6efd", color: "#ffffff", border: "1px solid #0d6efd" };
    default:
      return { backgroundColor: "#0d6efd", color: "#ffffff", border: "1px solid #0d6efd" };
  }
};

const toTitleCaseWords = (value) =>
  String(value || "")
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const getEphStatusLabel = (ephStatus) => {
  if (ephStatus === true) {
    return "Success";
  }

  if (ephStatus === false) {
    return "Failed";
  }

  return "N/A";
};

const toApiDateTime = (dateValue, endOfDay = false) => {
  const value = String(dateValue || "").trim();
  if (!value) {
    return "";
  }

  // Already in API datetime format.
  if (value.includes(" ")) {
    return value;
  }

  return `${value} ${endOfDay ? "23:59:59" : "00:00:00"}`;
};

const tableCellSx = {
  padding: "10px 8px",
  fontSize: "0.82rem",
  lineHeight: 1.3,
  wordBreak: "break-word",
  overflowWrap: "anywhere",
  whiteSpace: "normal",
  overflow: "hidden",
  borderRight: "1px solid rgba(0, 0, 0, 0.06)",
};

const tableHeaderSx = {
  ...tableCellSx,
  fontSize: "0.78rem",
  fontWeight: 700,
};

const TrackHca = (props) => {
  const STATUS_OPTIONS = [
    "Matched",
    "Not Found",
    "Pending",
    "Expired",
    "Error",
  ];

  const userInfo = useContext(UserContext);
  // const canAccessHCARequests = true;
  const canAccessHCARequests = Boolean(userInfo?.canAccessHCARequests);
  const [popMsg] = useState(popUpMessage.noUserAccess);
  const renderHTML = (html) => ({ __html: html });

  const [allData, setAllData] = useState([]);
  const [data, setData] = useState([]);
  const [progressFlag, setProgressFlag] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [errorFlag, setErrorFlag] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [noData, setNoData] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [expanded, setExpanded] = useState(false);
  const [nextToken, setNextToken] = useState(null);
  const [isInitialDateRange, setIsInitialDateRange] = useState(true);
  const [searchData, setSearchData] = useState({
    start_date: "",
    end_date: "",
    request_id: "",
    hcp_enterprise_id: "",
    status: "",
    okta_id: "",
    one_key_id: "",
    eph_status: "",
  });
  const [maxDate, setMaxDate] = useState("");
  const [minDate, setMinDate] = useState("");

  const updateDataState = (records, updateExportData = true, appendMode = false) => {
    if (Array.isArray(records) && records.length > 0) {
      if (appendMode) {
        setData((prevData) => [...prevData, ...records]);
        if (updateExportData) {
          setAllData((prevAll) => [...prevAll, ...records]);
        }
      } else {
        if (updateExportData) {
          setAllData(records);
        }
        setData(records);
      }
      setNoData(false);
    } else if (!appendMode) {
      if (updateExportData) {
        setAllData([]);
      }
      setData([]);
      setNoData(true);
    }
  };

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const handleSearch = (event, name, type) => {
    setIsInitialDateRange(false);
    
    if (type === "date") {
      if (!event) {
        setSearchData((prev) => ({
          ...prev,
          [name]: "",
        }));
        return;
      }

      const year = event.$y;
      const month = event.$M < 9 ? `0${event.$M + 1}` : `${event.$M + 1}`;
      const day = event.$D < 10 ? `0${event.$D}` : `${event.$D}`;
      const nextDate = `${year}-${month}-${day}`;

      setSearchData((prev) => ({
        ...prev,
        [name]: nextDate,
      }));

      if (name === "start_date" && !searchData.end_date) {
        setMinDate(`${year}/${month}/${day}`);
      } else if (name === "end_date" && !searchData.start_date) {
        setMaxDate(`${year}/${month}/${day}`);
      }
      return;
    }

    const { name: inputName, value } = event.target;
    setSearchData((prev) => ({
      ...prev,
      [inputName]: value,
    }));
  };

  const clearAll = () => {
    setIsInitialDateRange(false);
    
    const emptyFilters = {
      start_date: "",
      end_date: "",
      request_id: "",
      hcp_enterprise_id: "",
      status: "",
      okta_id: "",
      one_key_id: "",
      eph_status: "",
    };

    setMinDate("");
    setMaxDate("");
    setSearchData(emptyFilters);
    setPage(0);
    setProgressFlag(true);
    setErrorFlag(false);
    setErrorMsg("");
    setNextToken(null);

    Api.fetchHcaRequests()
      .then((response) => {
        const records = response?.data?.data;
        const token = response?.data?.next_token || null;
        updateDataState(records, true);
        setNextToken(token);
        setProgressFlag(false);
      })
      .catch((error) => {
        const msg =
          error?.response?.data?.errorCode || "Internal Server Error!";
        setErrorMsg(msg);
        setErrorFlag(true);
        setProgressFlag(false);
      });
  };

  const applyFilters = () => {
    setIsInitialDateRange(false);
    
    const params = {
      ...(searchData.okta_id ? { okta_id: searchData.okta_id.trim() } : {}),
      ...(searchData.one_key_id ? { one_key_id: searchData.one_key_id.trim() } : {}),
      ...(searchData.request_id ? { request_id: searchData.request_id.trim() } : {}),
      ...(searchData.hcp_enterprise_id
        ? { hcp_enterprise_id: searchData.hcp_enterprise_id.trim() }
        : {}),
      ...(searchData.status
        ? { status: toTitleCaseWords(searchData.status.trim()) }
        : {}),
      ...(searchData.eph_status !== ""
        ? { eph_status: searchData.eph_status }
        : {}),
      ...(searchData.start_date
        ? { start_date: searchData.start_date }
        : {}),
      ...(searchData.end_date
        ? { end_date: searchData.end_date }
        : {}),
    };

    setProgressFlag(true);
    setErrorFlag(false);
    setErrorMsg("");
    setNextToken(null);

    Api.fetchHcaRequests(params)
      .then((response) => {
        const records = response?.data?.data;
        const token = response?.data?.next_token || null;
        updateDataState(records, true);
        setNextToken(token);
        setProgressFlag(false);
        setPage(0);
      })
      .catch((error) => {
        const msg =
          error?.response?.data?.errorCode || "Internal Server Error!";
        setErrorMsg(msg);
        setErrorFlag(true);
        setProgressFlag(false);
      });
  };

  const loadMoreData = async (newPage) => {
    if (!nextToken) {
      setPage(newPage);
      return;
    }

    const params = {
      ...(searchData.okta_id ? { okta_id: searchData.okta_id.trim() } : {}),
      ...(searchData.one_key_id ? { one_key_id: searchData.one_key_id.trim() } : {}),
      ...(searchData.request_id ? { request_id: searchData.request_id.trim() } : {}),
      ...(searchData.hcp_enterprise_id
        ? { hcp_enterprise_id: searchData.hcp_enterprise_id.trim() }
        : {}),
      ...(searchData.status
        ? { status: toTitleCaseWords(searchData.status.trim()) }
        : {}),
      ...(searchData.eph_status !== ""
        ? { eph_status: searchData.eph_status }
        : {}),
      ...(searchData.start_date
        ? { start_date: searchData.start_date }
        : {}),
      ...(searchData.end_date
        ? { end_date: searchData.end_date }
        : {}),
      next_token: nextToken,
    };

    setIsLoadingMore(true);
    setErrorFlag(false);
    setErrorMsg("");

    Api.fetchHcaRequests(params)
      .then((response) => {
        const records = response?.data?.data;
        const token = response?.data?.next_token || null;
        updateDataState(records, true, true);
        setNextToken(token);
        setIsLoadingMore(false);
        setPage(newPage);
      })
      .catch((error) => {
        const msg =
          error?.response?.data?.errorCode || "Internal Server Error!";
        setErrorMsg(msg);
        setErrorFlag(true);
        setIsLoadingMore(false);
      });
  };

  const handleChangePage = (event, newPage) => {
    if (page === Math.ceil(data?.length / rowsPerPage) - 1) {
      if (nextToken) {
        loadMoreData(newPage);
      } else {
        setPage(newPage);
      }
    } else {
      setPage(newPage);
    }
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const goBack = () => {
    props.history.goBack();
  };

  useEffect(() => {
    const today = new Date();
    const sevenDaysAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);

    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = date.getMonth() < 9 ? `0${date.getMonth() + 1}` : `${date.getMonth() + 1}`;
      const day = date.getDate() < 10 ? `0${date.getDate()}` : `${date.getDate()}`;
      return `${year}-${month}-${day}`;
    };

    const startDate = formatDate(sevenDaysAgo);
    const endDate = formatDate(today);

    const initialSearchData = {
      start_date: startDate,
      end_date: endDate,
      request_id: "",
      hcp_enterprise_id: "",
      status: "",
      okta_id: "",
      one_key_id: "",
      eph_status: "",
    };

    setSearchData(initialSearchData);
    setProgressFlag(true);
    setNextToken(null);

    const params = {
      start_date: startDate,
      end_date: endDate,
    };

    Api.fetchHcaRequests(params)
      .then((response) => {
        const records = response?.data?.data;
        const token = response?.data?.next_token || null;
        updateDataState(records);
        setNextToken(token);
        setErrorFlag(false);
        setErrorMsg("");
        setProgressFlag(false);
      })
      .catch((error) => {
        const msg =
          error?.response?.data?.errorCode || "Internal Server Error!";
        setErrorMsg(msg);
        setErrorFlag(true);
        setProgressFlag(false);
      });
  }, []);

  const paginationStart = page * rowsPerPage;
  const paginationEnd = page * rowsPerPage + rowsPerPage;
  const pagedData = data.slice(paginationStart, paginationEnd);
  const hasMoreData = Boolean(nextToken);
  const paginationCount = noData ? 0 : data.length + (hasMoreData ? rowsPerPage : 0);

  const labelDisplayedRows = ({ from, to, count }) => {
    if (hasMoreData) {
      const upperBound = Math.min(to, data.length);
      return `${from}-${upperBound} of ${data.length}+`;
    }

    return `${from}-${to} of ${count}`;
  };

  return (
    <div className="container body-height extra-margin-top-less">
      {!canAccessHCARequests && (
        <section className="container extra-margin-top-less">
          <Paper square className="mb-2">
            <Tabs
              TabIndicatorProps={{ style: { background: "#CE2A1D" } }}
              value={0}
              textColor="primary"
              aria-label="no access tab"
            >
              <Tab className="_wg-dynamic-text" label="No Access" />
            </Tabs>
          </Paper>
          <div className="container alert alert-warning _wg-dynamic-text">
            <div dangerouslySetInnerHTML={renderHTML(popMsg[0].noHcaAccess.description)} />
          </div>
        </section>
      )}

      {canAccessHCARequests && progressFlag && (
        <div className="d-flex justify-content-center extra-margin-top">
          <ProgressBar />
        </div>
      )}

      {canAccessHCARequests && !progressFlag && !errorFlag && (
        <>
          <Card
            style={{
              cursor: "pointer",
              height: "auto",
              maxHeight: "auto",
              border: "1px solid grey",
              minWidth: "50%",
              margin: "auto",
              maxWidth: "90%",
            }}
            className="_wg-dynamic-text"
          >
            <CardActions disableSpacing className="p-0">
              <CardHeader
                subheader={
                  <Typography
                    variant="div"
                    style={{
                      fontSize: "20px",
                      fontWeight: `${expanded ? "bold" : "normal"}`,
                    }}
                  >
                    Advanced Search
                  </Typography>
                }
              />

              <ExpandMore
                expand={expanded}
                onClick={handleExpandClick}
                aria-expanded={expanded}
                aria-label="show more"
              >
                <Tooltip
                  disableFocusListener
                  title={expanded ? "Collapse" : "Open"}
                  id="tooltip"
                >
                  <ExpandMoreIcon />
                </Tooltip>
              </ExpandMore>
            </CardActions>

            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <CardContent sx={{ flex: "1 0 auto", fontSize: "15px", margin: "auto" }}>
                <Grid container spacing={3}>
                  <Grid item md={3} sm={6} xs={12}>
                    <DateTranslatedPicker
                      id="start_date"
                      label="Start Date"
                      maxDate={maxDate}
                      name="start_date"
                      value={searchData.start_date || null}
                      handleSearch={(event, fieldName, type) =>
                        handleSearch(event, fieldName, type)
                      }
                      className="admin-tracker-input"
                    />
                  </Grid>

                  <Grid item md={3} sm={6} xs={12}>
                    <DateTranslatedPicker
                      id="end_date"
                      label="End Date"
                      maxDate={maxDate}
                      minDate={minDate}
                      name="end_date"
                      value={searchData.end_date || null}
                      handleSearch={(event, fieldName, type) =>
                        handleSearch(event, fieldName, type)
                      }
                      className="admin-tracker-input"
                    />
                  </Grid>

                  <Grid item md={3} sm={6} xs={12}>
                    <TextField
                      fullWidth
                      label="Request Id"
                      size="small"
                      name="request_id"
                      value={searchData.request_id}
                      onChange={handleSearch}
                      className="admin-tracker-input"
                    />
                  </Grid>

                  <Grid item md={3} sm={6} xs={12}>
                    <TextField
                      fullWidth
                      label="HCP Enterprise Id"
                      size="small"
                      name="hcp_enterprise_id"
                      value={searchData.hcp_enterprise_id}
                      onChange={handleSearch}
                      className="admin-tracker-input"
                    />
                  </Grid>

                  <Grid item md={3} sm={6} xs={12}>
                    <FormControl fullWidth className="_wg-dynamic-text" size="small">
                      <InputLabel id="hca-status-filter">Status</InputLabel>
                      <Select
                        labelId="hca-status-filter"
                        id="action"
                        label="Status"
                        name="status"
                        value={searchData.status}
                        onChange={handleSearch}
                        className="_wg-dynamic-text"
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: "200px",
                              width: "auto",
                            },
                          },
                          getContentAnchorEl: null,
                          anchorOrigin: {
                            vertical: "bottom",
                            horizontal: "left",
                          },
                          transformOrigin: {
                            vertical: "top",
                            horizontal: "left",
                          },
                        }}
                      >
                        <MenuItem className="_wg-dynamic-text" value="">
                          <em>All</em>
                        </MenuItem>
                        {STATUS_OPTIONS.map((status) => (
                          <MenuItem className="_wg-dynamic-text" key={status} value={status}>
                            {status}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item md={3} sm={6} xs={12}>
                    <TextField
                      fullWidth
                      label="Okta Id/Passport Id"
                      size="small"
                      name="okta_id"
                      value={searchData.okta_id}
                      onChange={handleSearch}
                      className="admin-tracker-input"
                    />
                  </Grid>

                  <Grid item md={3} sm={6} xs={12}>
                    <TextField
                      fullWidth
                      label="One Key Id"
                      size="small"
                      name="one_key_id"
                      value={searchData.one_key_id}
                      onChange={handleSearch}
                      className="admin-tracker-input"
                    />
                  </Grid>

                  <Grid item md={3} sm={6} xs={12}>
                    <FormControl fullWidth className="_wg-dynamic-text" size="small">
                      <InputLabel id="hca-eph-status-filter">EPH Status</InputLabel>
                      <Select
                        labelId="hca-eph-status-filter"
                        id="eph_status"
                        label="EPH Status"
                        name="eph_status"
                        value={searchData.eph_status}
                        onChange={handleSearch}
                        className="_wg-dynamic-text"
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: "200px",
                              width: "auto",
                            },
                          },
                          getContentAnchorEl: null,
                          anchorOrigin: {
                            vertical: "bottom",
                            horizontal: "left",
                          },
                          transformOrigin: {
                            vertical: "top",
                            horizontal: "left",
                          },
                        }}
                      >
                        <MenuItem className="_wg-dynamic-text" value="">
                          <em>All</em>
                        </MenuItem>
                        <MenuItem className="_wg-dynamic-text" value={true}>
                          Success
                        </MenuItem>
                        <MenuItem className="_wg-dynamic-text" value={false}>
                          Failed
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item md={12} sm={12} xs={12}>
                    <Button
                      className="btn btn-secondry border clearall"
                      onClick={clearAll}
                    >
                      Clear Filter
                    </Button>
                    <Button
                      className="btn btn-secondry border request nextButton ml-3"
                      onClick={applyFilters}
                    >
                      Search
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Collapse>
          </Card>

          <Paper square className="mb-2 mt-5">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 20px" }}>
              <Typography variant="h6" className="fw-bold">
                HCA Requests
              </Typography>
              <div style={{ flex: 1, display: "flex", justifyContent: "center" }}>
                {isInitialDateRange && searchData.start_date && searchData.end_date && (
                  <Typography variant="body2" sx={{ color: "#856404", fontWeight: "500" }}>
                    Showing: All Records from {searchData.start_date} to {searchData.end_date}
                  </Typography>
                )}
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "8px" }}>
                <TrackHcaExportFile
                  csvData={allData}
                  fileName={`TrackHcaExport-${new Date()
                    .toISOString()
                    .replace(/[-:]/g, "")
                    .slice(0, 15)}`}
                />
              </div>
            </div>
          </Paper>

          <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
            <Table aria-label="hca requests table" sx={{ tableLayout: "fixed", width: "100%", minWidth: "1280px" }}>
              <TableHead id="track-head">
                <TableRow>
                  <TableCell className="font" align="center" sx={{ ...tableHeaderSx, width: "22%" }}>
                    <Typography variant="subtitle2" className="fw-bold" sx={{ fontSize: "0.78rem" }}>
                      Request ID
                    </Typography>
                  </TableCell>
                  <TableCell className="font" align="center" sx={{ ...tableHeaderSx, width: "12%" }}>
                    <Typography
                      variant="subtitle2"
                      className="fw-bold"
                      sx={{ fontSize: "0.78rem" }}
                    >
                      HCP Enterprise ID
                    </Typography>
                  </TableCell>
                  <TableCell className="font" align="center" sx={{ ...tableHeaderSx, width: "12.25%" }}>
                    <Typography variant="subtitle2" className="fw-bold" sx={{ fontSize: "0.78rem" }}>
                      Status
                    </Typography>
                  </TableCell>
                  <TableCell className="font" align="center" sx={{ ...tableHeaderSx, width: "11%" }}>
                    <Typography variant="subtitle2" className="fw-bold" sx={{ fontSize: "0.78rem" }}>
                      EPH Status
                    </Typography>
                  </TableCell>
                  <TableCell className="font" align="center" sx={{ ...tableHeaderSx, width: "11%" }}>
                    <Typography variant="subtitle2" className="fw-bold" sx={{ fontSize: "0.78rem" }}>
                      Okta ID/Passport ID
                    </Typography>
                  </TableCell>
                  <TableCell className="font" align="center" sx={{ ...tableHeaderSx, width: "11%" }}>
                    <Typography variant="subtitle2" className="fw-bold" sx={{ fontSize: "0.78rem" }}>
                      One Key ID
                    </Typography>
                  </TableCell>
                  <TableCell className="font" align="center" sx={{ ...tableHeaderSx, width: "12%" }}>
                    <Typography variant="subtitle2" className="fw-bold" sx={{ fontSize: "0.78rem" }}>
                      Created Date
                    </Typography>
                  </TableCell>
                  <TableCell className="font" align="center" sx={{ ...tableHeaderSx, width: "12%" }}>
                    <Typography variant="subtitle2" className="fw-bold" sx={{ fontSize: "0.78rem" }}>
                      Updated Date
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {noData || data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={tableCellSx}>
                      No Records Found
                    </TableCell>
                  </TableRow>
                ) : (
                  pagedData.map((row, index) => (
                    <TableRow key={row.request_id || index}>
                      <TableCell align="center" sx={{ ...tableCellSx, padding: "10px 4px" }}>{row.request_id || "N/A"}</TableCell>
                      <TableCell align="center" sx={tableCellSx}>{row.hcp_enterprise_id || "N/A"}</TableCell>
                      <TableCell align="center" sx={tableCellSx}>
                        <span
                          style={{
                            position: "relative",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "100%",
                          }}
                        >
                          <span
                            className="rounded py-2 px-3"
                            style={{
                              display: "inline-block",
                              minWidth: "72px",
                              maxWidth: "100%",
                              textAlign: "center",
                              fontSize: "0.78rem",
                              padding: "4px 8px",
                              ...getStatusStyle(row.status),
                            }}
                          >
                            {row.status ? getStatusLabel(row.status) : "N/A"}
                          </span>
                          {isNotFoundStatus(row.status) && (
                            <Tooltip
                              disableFocusListener
                              title={row.manual_rejection_reason || "No reason provided"}
                              id="tooltip"
                            >
                              <InfoOutlinedIcon
                                sx={{
                                  fontSize: "16px",
                                  color: "#6c757d",
                                  position: "absolute",
                                  right: "1px",
                                }}
                              />
                            </Tooltip>
                          )}
                        </span>
                      </TableCell>
                      <TableCell align="center" sx={tableCellSx}>
                        <span
                          style={{
                            position: "relative",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "100%",
                          }}
                        >
                          {getEphStatusLabel(row.eph_status)}
                          {row.eph_status === false && (
                            <Tooltip
                              disableFocusListener
                              title={row.reason || "No reason provided"}
                              id="tooltip"
                            >
                              <InfoOutlinedIcon
                                sx={{
                                  fontSize: "16px",
                                  color: "#6c757d",
                                  position: "absolute",
                                  right: "1px",
                                }}
                              />
                            </Tooltip>
                          )}
                        </span>
                      </TableCell>
                      <TableCell align="center" sx={tableCellSx}>{row.okta_id || row.passport_id || "N/A"}</TableCell>
                      <TableCell align="center" sx={tableCellSx}>{row.one_key_id || "N/A"}</TableCell>
                      <TableCell align="center" sx={tableCellSx}>{row.created_date || "N/A"}</TableCell>
                      <TableCell align="center" sx={tableCellSx}>{row.updated_date || "N/A"}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[10, 20, 25, 50]}
            component="div"
            count={paginationCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelDisplayedRows={labelDisplayedRows}
            id="track-head"
            backIconButtonProps={{ disabled: page === 0 }}
            nextIconButtonProps={{
              disabled:
                isLoadingMore || (!nextToken && page === Math.ceil(data.length / rowsPerPage) - 1),
            }}
          />
          {isLoadingMore && (
            <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "8px", padding: "0 16px 12px" }}>
              <CircularProgress size={14} />
              <Typography variant="caption" color="textSecondary">
                Loading more records...
              </Typography>
            </div>
          )}
        </>
      )}

      {canAccessHCARequests && errorFlag && !progressFlag && (
        <div className="d-flex justify-content-center extra-margin-top">
          <Alert severity="error" className="container">
            <AlertTitle id="alert-text">{errorMsg}</AlertTitle>
            <strong
              className="link pointer text-primary"
              id="alert-text"
              onClick={goBack}
            >
              back!
            </strong>
          </Alert>
        </div>
      )}
    </div>
  );
};

export default TrackHca;
